<template>
  <router-link v-bind:to="{ name: hrefInfo.name, params: hrefInfo.params }" class="no-hover">
    <div class="featured-article">
      <div
        class="featured-article__image"
        :style="imageStyles"
      />

      <div class="featured-article__header" ref="articleHeader">
        <span
          class="featured-article__category category"
          v-if="article.category"
        >
          {{article.category}}
        </span>
        <h2 class="featured-article__title title">{{article.title}}</h2>
      </div>

      <div class="featured-article__info" ref="articleInfo">
        <p class="featured-article__standfirst standfirst">{{article.standfirst}}</p>
        <link-with-button-below
          :data="{
            text: 'Read More',
            href: hrefInfo
          }"
        />
      </div>
    </div>
  </router-link>
</template>

<script>
// Libs
import Vue from 'vue';

// Components
import LinkWithButtonBelow from '@/components/linkWithButtonBelow';
import { Bus } from '@/helpers/bus';

export default {

  components: {
    LinkWithButtonBelow,
  },

  props: ['article'],

  data() {
    return {
      headerHeight: 0,
      infoHeight: 0,
    };
  },

  computed: {

    hrefInfo() {
      return {
        name: 'matter-article',
        params: {
          slug: this.article.slug,
        },
      };
    },

    isDesktop() {
      return this.$store.getters.isDesktop;
    },

    currentWidth() {
      return this.$store.state.width;
    },

    imageStyles() {
      const styles = {
        backgroundImage: `url(${this.article.image.imageSrc})`,
      };
      if (this.isDesktop) {
        styles.height = `${this.headerHeight + this.infoHeight + 37}px`;
      }
      return styles;
    },

  },

  methods: {

    updateHeaderClientHeight() {
      const el = this.$refs.articleHeader;
      this.headerHeight = (el) ? el.clientHeight : 0;
    },

    updateInfoClientHeight() {
      const el = this.$refs.articleInfo;
      this.infoHeight = (el) ? el.clientHeight : 0;
    },

    measureLayout() {
      this.updateHeaderClientHeight();
      this.updateInfoClientHeight();
    },

  },

  watch: {

    currentDevice(/* newVal, oldVal */) {
      Vue.nextTick(() => {
        this.measureLayout();
      });
    },

    currentWidth(/* newVal, oldVal */) {
      Vue.nextTick(() => {
        this.measureLayout();
      });
    },

  },

  mounted() {
    Bus.$emit('rendered-article');
    this.measureLayout();
  },

};
</script>

<style lang="scss">
@import '~@/styles/_grid.scss';
@import '~@/styles/_typography.scss';
@import '~@/styles/_mixins.scss';

.featured-article {
  background-color: white;

  // media query props
  @include ngGrid('mobile');
  margin-top: rem(62px);
  margin-bottom: rem(110px);

  @include ngMq($nbp--tablet, up) {
    @include ngGrid('tablet');
    grid-template-columns: 50% 2.5rem 1fr;
    grid-gap: 0;
    margin-top: rem(70px);
    padding-left: 0;
    margin-bottom: rem(110px);
  }
  @include ngMq($nbp--desktop, up) {
    @include ngGrid('desktop');
    margin-left: rem(138px);
    margin-top: 0;
    padding-left: 0;
    margin-bottom: rem(170px);
  }

  &__image {
    background-size: cover;
    background-position: center center;
    position: absolute;

    width: 100%;
    height: rem(240px);

    @include ngMq($nbp--tablet, up) {
      grid-column: 1 / span 1;
      // height: rem(356px);
      height: 100%;
    }
    @include ngMq($nbp--desktop, up) {
      grid-column: 1 / span 7;
      height: rem(400px);
      margin-top: rem(90px);
      // height: 100%;
    }

    // background gradient
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(rgba(0,0,0,0), rgba(0,0,0,0.25), rgba(0,0,0,0.75));
    }

  }

  &__header {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    // media query props
    height: rem(240px);
    grid-column: 1 / -1;
    margin-left: 0;
    margin-bottom: 0;

    @include ngMq($nbp--tablet, up) {
      height: auto;
      margin-left: rem(-2px);
      grid-column: 3 / span 1;
      margin-bottom: 0.5rem;
    }
    @include ngMq($nbp--desktop, up) {
      margin-top: rem(90px);
      margin-left: rem(12px);
      grid-column: 8 / -1;
      margin-bottom: 0;
    }

    .category {
      text-transform: uppercase;

      // media query props
      @include sansserif($size: (10, 10, 4), $weight: 'regular');
      color: #F2F1EE;
      margin-bottom: 8px;

      @include ngMq($nbp--tablet, up) {
        @include sansserif($size: (13, 13, 5), $weight: 'regular');
        color: #B8C0C3;
        margin-bottom: 10px;
      }
      @include ngMq($nbp--desktop, up) {
        @include sansserif($size: (15, 15, 5), $weight: 'regular');
        color: #B8C0C3;
        margin-bottom: 26px;
      }
    }

    .title {
      @include type('article-heading');
      margin-right: 2rem;

      // media query props
      color: white;
      margin-bottom: 0.5rem;

      @include ngMq($nbp--tablet, up) {
        color: black;
        margin-bottom: 0;
      }
      @include ngMq($nbp--desktop, up) {
        color: black;
        margin-bottom: 0;
      }
    }
  }

  &__info {
    // media query props
    grid-column: 1 / -1;
    margin-left: 0;

    @include ngMq($nbp--tablet, up) {
      grid-column: 3 / span 1;
      margin-left: rem(-2px);
    }
    @include ngMq($nbp--desktop, up) {
      grid-column: 8 / -1;
      margin-left: rem(12px);
    }

    .standfirst {
      color: $color--primary-dark;

      // media query props
      @include sansserif($size: (13, 22, 0), $weight: 'light');
      margin: rem(-12px) 0 rem(23px) 0;

      @include ngMq($nbp--tablet, up) {
        @include sansserif($size: (14, 25, 0), $weight: 'light');
        margin: rem(2px) 0 rem(25px) 0;
      }
      @include ngMq($nbp--desktop, up) {
        @include sansserif($size: (18, 32, 0), $weight: 'light');
        margin: rem(-7px) 0 rem(45px) 0;
      }

    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/matter/FeaturedArticle.vue